/**
 * SetupChecklist - bloc opt-in affichant l etat de configuration du tenant.
 *
 * Remplace le WelcomeWizard popup intrusif (retire v2026.06).
 *
 * 4 items :
 *   1. Langue         - notificationLanguage des emails defini ?
 *   2. Email technique - techContact defini ?
 *   3. Connexion M365 - tenant M365 connecte (consent OAuth fait) ?
 *   4. Activation licence - licence active (pas en trial) ?
 *
 * Comportement :
 *   - Si tout est OK, le bloc se cache automatiquement
 *   - Sinon, affiche les items non-completes en priorite (ceux done en bas, gris)
 *   - Dismissible : un X stocke un flag localStorage qui cache le bloc pour cette session
 *     (re-affichage au prochain F5 si toujours incomplete)
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Card, Body1, Caption1, Button, tokens,
} from '@fluentui/react-components';
import {
  CheckmarkCircle24Filled,
  Circle24Regular,
  Globe20Regular,
  Mail20Regular,
  CloudArrowUp20Regular,
  Key20Regular,
  Dismiss20Regular,
} from '@fluentui/react-icons';
import { onboardingApi } from '../api/onboardingApi';
import { clientLicenseApi } from '../api/licenseApi';

const DISMISS_KEY = 'nis365:setup-checklist-dismissed';

interface ChecklistItem {
  id:          string;
  title:       string;
  description: string;
  done:        boolean;
  icon:        React.ReactNode;
  ctaLabel:    string;
  ctaHref:     string;
}

export function SetupChecklist() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });

  // Source de verite : onboarding status (connexion M365 + techContact + langue)
  const onboardingQ = useQuery({
    queryKey:  ['onboarding-status'],
    queryFn:   () => onboardingApi.status(),
    staleTime: 30_000,
    retry:     false,
  });

  // Source de verite : licence active
  const licenseQ = useQuery({
    queryKey:  ['client-license-status'],
    queryFn:   () => clientLicenseApi.status(),
    staleTime: 30_000,
    retry:     false,
  });

  // Tant qu une des 2 queries n a pas charge, on ne rend rien (evite un flicker)
  if (onboardingQ.isLoading || licenseQ.isLoading) {
    return null;
  }

  const onboarding = onboardingQ.data;
  const license    = licenseQ.data;

  const items: ChecklistItem[] = [
    {
      id:          'language',
      title:       t('setupChecklist.items.language.title', { defaultValue: 'Langue des emails' }),
      description: t('setupChecklist.items.language.description', { defaultValue: 'Définissez la langue des emails de notification.' }),
      done:        !!onboarding?.notificationLanguage,
      icon:        <Globe20Regular />,
      ctaLabel:    t('setupChecklist.cta.configure', { defaultValue: 'Configurer' }),
      ctaHref:     '/settings#language',
    },
    {
      id:          'techContact',
      title:       t('setupChecklist.items.techContact.title', { defaultValue: 'Email technique' }),
      description: t('setupChecklist.items.techContact.description', { defaultValue: 'Adresse email recevant les alertes techniques.' }),
      done:        !!onboarding?.techContact,
      icon:        <Mail20Regular />,
      ctaLabel:    t('setupChecklist.cta.configure', { defaultValue: 'Configurer' }),
      ctaHref:     '/settings#techContact',
    },
    {
      id:          'm365',
      title:       t('setupChecklist.items.m365.title', { defaultValue: 'Connexion Microsoft 365' }),
      description: t('setupChecklist.items.m365.description', { defaultValue: 'Autorisez NIS365 à auditer votre tenant via Microsoft Graph.' }),
      done:        onboarding?.connected === true && onboarding?.needsOnboarding === false,
      icon:        <CloudArrowUp20Regular />,
      ctaLabel:    t('setupChecklist.cta.connect', { defaultValue: 'Connecter' }),
      ctaHref:     '/onboarding',
    },
    {
      id:          'license',
      title:       t('setupChecklist.items.license.title', { defaultValue: 'Activation de la licence' }),
      description: t('setupChecklist.items.license.description', { defaultValue: 'Saisissez votre clé de licence pour débloquer le plan.' }),
      done:        license?.hasLicense === true && license?.mode === 'paid' && license?.valid === true,
      icon:        <Key20Regular />,
      ctaLabel:    t('setupChecklist.cta.activate', { defaultValue: 'Activer' }),
      ctaHref:     '/billing',
    },
  ];

  const doneCount  = items.filter((i) => i.done).length;
  const total      = items.length;
  const allDone    = doneCount === total;

  // Cache si tout est ok OU si user a dismiss
  if (allDone || dismissed) return null;

  // Tri : non-done en haut, done en bas en grise
  const sorted = [...items].sort((a, b) => Number(a.done) - Number(b.done));

  function handleDismiss() {
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* */ }
    setDismissed(true);
  }

  return (
    <Card style={{ marginBottom: 24, padding: 0, border: `1px solid ${tokens.colorNeutralStroke2}` }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '14px 20px',
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        background: tokens.colorNeutralBackground2,
      }}>
        <div>
          <Body1 style={{ fontWeight: 600 }}>
            {t('setupChecklist.title', { defaultValue: 'Configuration de votre instance' })}
          </Body1>
          <Caption1 style={{ display: 'block', color: tokens.colorNeutralForeground2, marginTop: 2 }}>
            {t('setupChecklist.progress', { done: doneCount, total, defaultValue: `${doneCount} sur ${total} étapes complétées` })}
          </Caption1>
        </div>
        <Button
          appearance="subtle"
          icon={<Dismiss20Regular />}
          onClick={handleDismiss}
          aria-label={t('setupChecklist.dismiss', { defaultValue: 'Masquer' })}
          title={t('setupChecklist.dismissTitle', { defaultValue: 'Masquer jusqu au prochain rechargement' })}
        />
      </div>

      <div style={{
        display:             'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap:                 12,
        padding:             16,
      }}>
        {sorted.map((item) => (
          <ChecklistItemCard key={item.id} item={item} onAction={() => navigate(item.ctaHref)} />
        ))}
      </div>
    </Card>
  );
}

function ChecklistItemCard({ item, onAction }: { item: ChecklistItem; onAction: () => void }) {
  const color = item.done ? tokens.colorPaletteGreenForeground1 : tokens.colorBrandForeground1;
  return (
    <div style={{
      padding:      14,
      border:       `1px solid ${item.done ? tokens.colorPaletteGreenBorder2 : tokens.colorNeutralStroke2}`,
      borderRadius: 6,
      background:   item.done ? tokens.colorPaletteGreenBackground1 : tokens.colorNeutralBackground1,
      opacity:      item.done ? 0.7 : 1,
      display:      'flex',
      flexDirection:'column',
      gap:          8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {item.done
          ? <CheckmarkCircle24Filled style={{ color, flexShrink: 0 }} />
          : <Circle24Regular style={{ color: tokens.colorNeutralForeground3, flexShrink: 0 }} />}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
          <span style={{ color, display: 'flex' }}>{item.icon}</span>
          <Body1 style={{ fontWeight: 600 }}>{item.title}</Body1>
        </div>
      </div>
      <Caption1 style={{ color: tokens.colorNeutralForeground2, minHeight: 32 }}>
        {item.description}
      </Caption1>
      {!item.done && (
        <Button size="small" appearance="primary" onClick={onAction} style={{ alignSelf: 'flex-start' }}>
          {item.ctaLabel}
        </Button>
      )}
    </div>
  );
}
